import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Skeleton,
  TablePagination,
  Typography,
} from '@mui/material'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { NewsArticleModal } from '@/components/news/NewsArticleModal'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { newsApi, type PublicNews } from '@/api/news'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'
import { ROUTES } from '@/utils/constants'
import '@/styles/landing.css'

const GUEST_PER_PAGE = 9

/**
 * Список всех новостей. Один маршрут на два контекста:
 * авторизованные видят его в кабинете, гости — в лендинговой обёртке.
 * `/news/:slug` — deep-link: тот же список плюс сразу открытая модалка
 * (адрес нужен для OG-превью ссылок в соцсетях).
 */
export function NewsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { slug } = useParams<{ slug?: string }>()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  const [items, setItems] = useState<PublicNews[]>([])
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Кабинет — постраничная навигация, лендинг — «показать ещё»
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [guestLoaded, setGuestLoaded] = useState(1)

  const signedQuery = location.search.startsWith('?') ? location.search.slice(1) : location.search

  const load = useCallback(
    async (targetPage: number, perPage: number, append: boolean) => {
      setLoading(true)
      setError(null)
      try {
        const result = await newsApi.getPublicPage(targetPage, perPage)
        setItems((prev) => (append ? [...prev, ...result.data] : result.data))
        setMeta(result.meta)
      } catch (err) {
        setError(getErrorMessage(err))
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (isAuthenticated) {
      void load(page + 1, rowsPerPage, false)
    } else {
      void load(1, GUEST_PER_PAGE, false)
      setGuestLoaded(1)
    }
  }, [isAuthenticated, page, rowsPerPage, load])

  const loadMoreForGuest = () => {
    const next = guestLoaded + 1
    setGuestLoaded(next)
    void load(next, GUEST_PER_PAGE, true)
  }

  const openArticle = (articleSlug: string) => {
    navigate(ROUTES.NEWS_VIEW(articleSlug))
  }

  const closeArticle = () => {
    navigate(ROUTES.NEWS, { replace: true })
  }

  const modal = (
    <NewsArticleModal slug={slug ?? null} onClose={closeArticle} signedQuery={signedQuery || undefined} />
  )

  // --- Кабинет ---
  if (isAuthenticated) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ mb: 2 }}
          >
            <Link
              to={ROUTES.DASHBOARD}
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {t('dashboard.home')}
            </Link>
            <Typography color="text.primary">{t('news.title')}</Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
              {t('news.title')}
            </Typography>
            <PageHelpPopover pageKey="news" />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i}>
                  <Skeleton variant="rectangular" height={140} />
                  <CardContent>
                    <Skeleton variant="text" width="80%" height={26} />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="60%" />
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : meta.total === 0 ? (
            <Typography color="text.secondary">{t('landing.news.empty')}</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
              {items.map((item) => (
                <Card key={item.slug}>
                  <CardActionArea
                    onClick={() => openArticle(item.slug)}
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    {item.cover_url && (
                      <CardMedia component="img" height="140" image={item.cover_url} alt={item.title} />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        {item.is_pinned && (
                          <Chip size="small" color="info" label={t('admin.news.pinned')} />
                        )}
                        {(item.tags ?? []).map((tag) => (
                          <Chip
                            key={tag.id}
                            size="small"
                            label={tag.name}
                            sx={{ bgcolor: tag.color, color: '#fff' }}
                          />
                        ))}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.preview}
                      </Typography>
                      {item.published_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {formatDateDMY(item.published_at)}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          )}

          {!loading && meta.total > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, overflow: 'hidden', minWidth: 0 }}>
              <TablePagination
                component="div"
                count={meta.total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 15, 20]}
                labelRowsPerPage={t('news.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) =>
                  t('news.displayedRows', { from, to, count })
                }
              />
            </Box>
          )}

          {modal}
        </Box>
      </DashboardLayout>
    )
  }

  // --- Гость: лендинговая обёртка ---
  const canLoadMore = meta.current_page < meta.last_page

  return (
    <div className="landing-page">
      <Header />
      <main className="news-page">
        <div className="container">
          <div className="news-page-header">
            <h1>{t('news.title')}</h1>
            <p>{t('landing.news.subtitle')}</p>
          </div>

          {loading && items.length === 0 ? (
            <div className="news-grid">
              {[1, 2, 3].map((i) => (
                <div key={i} className="news-card news-card-skeleton" aria-hidden>
                  <div className="news-skeleton-line news-skeleton-title" />
                  <div className="news-skeleton-line" />
                  <div className="news-skeleton-line" />
                  <div className="news-skeleton-line news-skeleton-short" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="news-page-empty">{t('landing.news.empty')}</p>
          ) : (
            <div className="news-grid">
              {items.map((item) => (
                <Link
                  key={item.slug}
                  to={ROUTES.NEWS_VIEW(item.slug)}
                  className="news-card news-card-link"
                >
                  {item.cover_url && (
                    <img className="news-card-cover" src={item.cover_url} alt={item.title} loading="lazy" />
                  )}
                  <h3>{item.title}</h3>
                  <p>{item.preview}</p>
                  {(item.tags?.length ?? 0) > 0 && (
                    <div className="news-card-tags">
                      {item.tags!.map((tag) => (
                        <span key={tag.id} className="news-card-tag" style={{ backgroundColor: tag.color }}>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="news-date">
                    {item.published_at ? formatDateDMY(item.published_at) : ''}
                  </div>
                </Link>
              ))}
            </div>
          )}

          {canLoadMore && (
            <div className="news-page-more">
              <button type="button" className="btn-primary" onClick={loadMoreForGuest} disabled={loading}>
                {loading ? t('common.loading') : t('news.loadMore')}
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
      {modal}
    </div>
  )
}
